import { useMemo } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import InfiniteScroll from "react-infinite-scroll-component";
import { formatDisplayNumber } from "utils/functions/number";
import { parseQueryString } from "utils/functions/parseQueryString";
import { adminService } from "apiServices/apiController/adminService";
import { Icons, TableCustom } from "components";
import { DepartmentMemberModalStyled } from "./styled";
import Loader from "react-loader-spinner";
import { QUERY_KEYS } from "utils/constants/queryKeys";

const DepartmentMemberModal = ({
  missionId,
  department,
  tab,
  onTabChange,
  onClose,
  fetchMembers,
}) => {
  const memberColumns = [
    {
      title: "รหัสพนักงาน",
      field: "employee_id",
      headerStyle: { width: "15%" },
      style: { width: "15%" },
    },
    {
      title: "ชื่อ - นามสกุล",
      field: "name",
      headerStyle: { flex: 1 },
      style: { flex: 1 },
      render: ({ value, origin }) => (
        <span className="member_name_cell">
          {origin?.profile_picture_uri ? (
            <img className="member_avatar" src={origin.profile_picture_uri} alt="" />
          ) : (
            <div className="member_avatar" />
          )}
          {value}
        </span>
      ),
    },
    {
      title: "ผลการทำภารกิจ",
      field: "total_achieved_amount",
      headerStyle: { width: "25%", textAlign: "right" },
      style: { width: "25%", textAlign: "right" },
      render: ({ value, origin }) => (
        <span className="member_steps">
          <span className="steps_value">{formatDisplayNumber(value)}</span>
          <span className="steps_unit"> {origin?.unit_category || "ก้าว"}</span>
        </span>
      ),
    },
  ];

  const defaultFetchMembers = useMemo(() => {
    if (fetchMembers) return fetchMembers;
    const perPage = 20;
    return async ({ pageParam = 0 }) => {
      const stringQuery = parseQueryString({
        status: tab,
        per_page: perPage,
        offset: pageParam,
      });
      const res = await adminService.GET_MISSION_V2_DEPARTMENT_MEMBERS(
        missionId,
        department?.id,
        stringQuery
      );
      if (res?.success) {
        const members = res.data?.members || [];
        return {
          data: members,
          meta: {
            department_name_th: res.data?.department_name_th,
            department_name_en: res.data?.department_name_en,
            total_participants: res.data?.participant_summary?.total_count
              ?? res.data?.total_participants
              ?? 0,
          },
          nextOffset: members.length >= perPage ? pageParam + perPage : undefined,
        };
      }
      return { data: [], meta: null, nextOffset: undefined };
    };
  }, [fetchMembers, missionId, department?.id, tab]);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isLoading,
  } = useInfiniteQuery({
    queryKey: [QUERY_KEYS.DEPARTMENT_MEMBERS, missionId, department?.id, tab],
    queryFn: ({ pageParam = 0 }) =>
      defaultFetchMembers({ departmentId: department?.id, tab, pageParam }),
    getNextPageParam: (lastPage) => lastPage.nextOffset,
    enabled: !!department && !!missionId,
    staleTime: 30 * 1000, //30วินาที
  });

  const allMembers = useMemo(
    () => data?.pages.flatMap((p) => p.data) ?? [],
    [data]
  );

  const meta = data?.pages?.[0]?.meta;

  return (
    <DepartmentMemberModalStyled>
      <button
        type="button"
        className="modal_close"
        onClick={onClose}
        aria-label="ปิด"
      >
        <Icons.CloseX3 />
      </button>

      <div className="modal_header">
        <div className="modal_title_block">
          <div className="dept_th">{meta?.department_name_th}</div>
          <div className="dept_en">{meta?.department_name_en}</div>
        </div>
        <div className="modal_divider" />
        <div className="modal_count_block">
          <div className="count_text_col">
            <div className="count_label_th">จำนวนผู้เข้าร่วม</div>
            <div className="count_label_en">Total Participants</div>
          </div>
          <div className="count_value">
            {formatDisplayNumber(meta?.total_participants ?? 0)} คน
          </div>
        </div>
      </div>

      <div className="modal_tabs">
        <button
          type="button"
          className={`modal_tab ${tab === "active" ? "modal_tab_active" : ""}`}
          onClick={() => onTabChange("active")}
        >
          สถานะผู้เข้าร่วม Active
        </button>
        <button
          type="button"
          className={`modal_tab ${tab === "inactive" ? "modal_tab_active" : ""}`}
          onClick={() => onTabChange("inactive")}
        >
          สถานะผู้เข้าร่วม Inactive
        </button>
      </div>

      <div className="modal_body_content">
        <InfiniteScroll
          dataLength={allMembers.length}
          next={fetchNextPage}
          hasMore={Boolean(hasNextPage)}
          height={411}
          loader={
            <div className="modal_loading">
              <Loader type="Puff" color="#575D96" height={70} width={70} />
            </div>
          }
        >
          <TableCustom.TableNormal
            theme_table_new
            fontColorHeader="#3E3E3E"
            paddingHeader="7.5px 0"
            columns={memberColumns}
            data={allMembers}
            loading={isLoading && allMembers.length === 0}
            labelEmpty="ไม่มีข้อมูล"
          />
        </InfiniteScroll>
      </div>
    </DepartmentMemberModalStyled>
  );
};

export default DepartmentMemberModal;
